interface TrustSectionProps {
  onCtaClick: () => void;
}

const credentials = [
  { icon: 'ri-award-line', text: 'Zertifiziertes EMS-Training' },
  { icon: 'ri-stethoscope-line', text: 'Ärztlich empfohlen' },
  { icon: 'ri-user-star-line', text: 'Qualifizierte Trainer' },
  { icon: 'ri-map-pin-2-line', text: 'Lokal in Pinneberg' },
];

const studioFacts = [
  { icon: 'ri-car-line', text: 'Parkplatz direkt vor dem Studio' },
  { icon: 'ri-calendar-check-line', text: 'Termine nach Vereinbarung' },
  { icon: 'ri-shield-user-line', text: 'Diskrete, ruhige Atmosphäre' },
  { icon: 'ri-heart-pulse-line', text: 'Gesundheits-orientiert' },
];

const medTrustPoints = [
  {
    icon: 'ri-stethoscope-line',
    title: 'Ärztlich empfohlen',
    text: 'EMS-Training wird in der Sportmedizin und Physiotherapie eingesetzt. Unsere Methodik ist medizinisch geprüft – auch bei Rückenproblemen, nach Verletzungen oder als Rehabilitation.',
  },
  {
    icon: 'ri-medal-line',
    title: 'Zertifizierte Trainer',
    text: 'Unser Team ist ausgebildet und zertifiziert – nicht nur im EMS-Training, sondern in der individuellen Betreuung von Menschen mit unterschiedlichsten Zielen und Ausgangssituationen.',
  },
  {
    icon: 'ri-building-2-line',
    title: 'Bewährtes System. Echter Mensch dahinter.',
    text: 'Als Teil des Körperformen-Netzwerks nutzen wir eine erprobte Methodik. Was in Pinneberg dazu kommt: ein persönlich geführtes Studio, das sich um jeden einzelnen Kunden wirklich kümmert.',
  },
];

export default function TrustSection({ onCtaClick }: TrustSectionProps) {
  return (
    <section id="ueber-uns" className="py-24 md:py-32 bg-white">
      <div className="max-w-6xl mx-auto px-6">

        {/* Top: Image + text */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-24 items-center mb-20">
          <div className="relative">
            <div className="relative overflow-hidden rounded-3xl w-full h-[420px] md:h-[560px]">
              <img
                src="https://storage.readdy-site.link/project_files/39a14c12-a9bc-487a-8116-959d368b9fcb/500f87a0-17f2-42f3-baf3-4547a7e19934_IMG-20260219-WA0098.jpg?v=3686b71a6b5dbc9f647d00f59cad3df7"
                alt="Das Team von Körperformen Pinneberg"
                className="w-full h-full object-cover"
                style={{ objectPosition: 'center 20%' }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5">
                <div className="grid grid-cols-2 gap-2">
                  {credentials.map((c, i) => (
                    <div
                      key={i}
                      className="rounded-xl px-3 py-2.5 flex items-center gap-2 backdrop-blur-sm"
                      style={{ backgroundColor: 'rgba(255,255,255,0.94)' }}
                    >
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                        <i className={`${c.icon} text-xs`} style={{ color: '#1A5C3D' }}></i>
                      </div>
                      <span
                        className="text-gray-700 text-xs font-medium leading-tight"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        {c.text}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div>
            <span
              className="text-xs font-medium tracking-[0.2em] uppercase"
              style={{ fontFamily: 'Inter, sans-serif', color: '#1A5C3D' }}
            >
              Das Studio · Das Team
            </span>
            <h2
              className="text-3xl md:text-[44px] font-bold text-gray-900 mt-4 leading-[1.1]"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Bewährtes Konzept.<br />
              Echter Mensch dahinter.<br />
              <em className="not-italic" style={{ color: '#6B7280' }}>Hier in Pinneberg.</em>
            </h2>

            <div className="mt-7 space-y-4">
              <p
                className="text-gray-600 text-base leading-relaxed"
                style={{ fontFamily: 'Inter, sans-serif', fontWeight: 300 }}
              >
                Körperformen ist ein erprobtes Konzept für effektives EMS-Training –
                eine Methodik, die in Dutzenden Studios funktioniert. Was in Pinneberg
                dazu kommt: ein Team, das wirklich hinter dem steht, was es tut,
                und persönlich für jeden Kunden da ist.
              </p>
              <p
                className="text-gray-600 text-base leading-relaxed"
                style={{ fontFamily: 'Inter, sans-serif', fontWeight: 300 }}
              >
                Unser Studio ist bewusst klein gehalten. Jede Einheit wird persönlich
                betreut, jeder Fortschritt begleitet. Jeder Mensch ist willkommen –
                ob 25 oder 68, ob Einsteiger oder sportlich aktiv.
              </p>
            </div>

            <div className="mt-7 grid grid-cols-2 gap-3">
              {studioFacts.map((f, i) => (
                <div
                  key={i}
                  className="flex items-start gap-2.5 rounded-xl px-3 py-3 border"
                  style={{ borderColor: 'rgba(26,92,61,0.14)', backgroundColor: 'rgba(26,92,61,0.025)' }}
                >
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className={`${f.icon} text-xs`} style={{ color: '#1A5C3D' }}></i>
                  </div>
                  <span
                    className="text-gray-700 text-xs leading-snug font-medium break-words min-w-0"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {f.text}
                  </span>
                </div>
              ))}
            </div>

            <div
              className="mt-7 p-6 rounded-2xl border-l-4"
              style={{ borderLeftColor: '#1A5C3D', backgroundColor: '#F4F7F5' }}
            >
              <p
                className="text-gray-800 text-lg leading-relaxed"
                style={{ fontFamily: 'Cormorant Garamond, serif', fontStyle: 'italic' }}
              >
                „Wir kennen den Namen jedes unserer Kunden.
                Das ist kein Zufall – das ist unser Anspruch."
              </p>
              <p className="text-gray-400 text-xs mt-3" style={{ fontFamily: 'Inter, sans-serif' }}>
                — Das Team von Körperformen Pinneberg
              </p>
            </div>

            <div className="mt-7 flex items-center gap-5 flex-wrap">
              <button
                onClick={onCtaClick}
                className="text-white font-semibold text-sm px-7 py-3.5 rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap"
                style={{ backgroundColor: '#1A5C3D', fontFamily: 'Inter, sans-serif' }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#1E6B47')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#1A5C3D')}
              >
                Uns kennenlernen →
              </button>
              <div className="flex items-center gap-2">
                <div className="flex gap-0.5">
                  {[1,2,3,4,5].map((s) => (
                    <div key={s} className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-star-fill text-yellow-500 text-xs"></i>
                    </div>
                  ))}
                </div>
                <span className="text-gray-400 text-xs" style={{ fontFamily: 'Inter, sans-serif' }}>5,0 · 20 Bewertungen</span>
              </div>
            </div>
          </div>
        </div>

        {/* Medical trust row */}
        <div>
          <div className="text-center mb-10">
            <span
              className="text-xs font-medium tracking-[0.18em] uppercase"
              style={{ fontFamily: 'Inter, sans-serif', color: '#1A5C3D' }}
            >
              Medizinische Kompetenz
            </span>
            <h3
              className="text-2xl md:text-3xl font-bold text-gray-900 mt-3 leading-snug"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Training, das man verantworten kann.
            </h3>
            <p
              className="text-gray-400 text-sm mt-3 max-w-md mx-auto leading-relaxed"
              style={{ fontFamily: 'Inter, sans-serif', fontWeight: 300 }}
            >
              EMS ist nicht Trend – es ist Medizintechnik. Wir nehmen das ernst.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {medTrustPoints.map((item, i) => (
              <div
                key={i}
                className="rounded-2xl p-7 border"
                style={{ backgroundColor: '#F7FAF8', borderColor: 'rgba(26,92,61,0.1)' }}
              >
                <div
                  className="w-10 h-10 flex items-center justify-center rounded-xl mb-5 flex-shrink-0"
                  style={{ backgroundColor: 'rgba(26,92,61,0.1)' }}
                >
                  <i className={`${item.icon} text-lg`} style={{ color: '#1A5C3D' }}></i>
                </div>
                <h4 className="text-gray-900 font-semibold text-base mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                  {item.title}
                </h4>
                <p className="text-gray-500 text-sm leading-relaxed" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 300 }}>
                  {item.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
